/**
 * Overdracht — projectleider draagt projecten tijdelijk over aan een collega.
 *
 * Tab "Overdragen": kies periode + wie waarneemt (per project aan te passen),
 * de tool genereert per project een dossier (stand van zaken uit taken/deadlines/
 * draaidagen/meeting-notes) dat je daarna kunt redigeren.
 * Tab "Aan mij overgedragen": de dossiers die een collega aan jou heeft overgedragen.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, Autocomplete, Checkbox,
  CircularProgress, Alert, Chip, Divider, Tab, Tabs, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { startActivity } from '../../utils/activityTracker';
import { formatDateISO } from '../../utils/weekUtils';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '?');
const todayIso = () => formatDateISO(new Date());
const plusDaysIso = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function Overdracht() {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const userEmail = (accounts[0]?.username || '').toLowerCase();
  const userName = accounts[0]?.name || userEmail.split('@')[0];

  const [tab, setTab] = useState(0);
  const [error, setError] = useState(null);

  // ── Overdragen (wizard) ──
  const [colleagues, setColleagues] = useState([]);
  const [gevers, setGevers] = useState({}); // email -> aantal actieve overdrachten
  // Zelfde alias-normalisatie als de rest van de module: @full-frame.nl telt mee.
  const geverAantal = (email) => {
    const e = String(email || '').toLowerCase();
    return gevers[e] || gevers[e.replace('@full-frame.nl', '@fullframe.nl')] || 0;
  };
  const [collegaPl, setCollegaPl] = useState(null); // filter: waarnemend PL
  const [collegaZoek, setCollegaZoek] = useState('');
  const [projects, setProjects] = useState([]);
  const [offers, setOffers] = useState([]); // openstaande offerte-trajecten (sales) van deze PL
  const [extraProjects, setExtraProjects] = useState([]); // handmatig toegevoegd via de zoeker
  const [showOlder, setShowOlder] = useState(false);      // oude nooit-afgesloten projecten inklappen
  const [searchOpts, setSearchOpts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounce = React.useRef(null);
  const searchProjects = (q) => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      if (!q || q.length < 2) { setSearchOpts([]); return; }
      setSearchLoading(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/overdracht/search-projects?q=${encodeURIComponent(q)}`);
        const d = await leesJson(r, 'Projecten zoeken');
        setSearchOpts(d.projects || []);
      } catch (e) { setError(e.message); setSearchOpts([]); }
      finally { setSearchLoading(false); }
    }, 300);
  };
  const addExtraProject = (p) => {
    if (!p) return;
    const known = [...projects, ...extraProjects].some((x) => x.id === p.id);
    if (!known) setExtraProjects((prev) => [...prev, p]);
    setSelected((prev) => new Set(prev).add(p.id));
  };
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selected, setSelected] = useState(new Set());       // project-ids
  const [overrides, setOverrides] = useState({});             // projectId → colleague
  const [defaultTo, setDefaultTo] = useState(null);           // colleague {name,email}
  const [periodStart, setPeriodStart] = useState(todayIso());
  const [periodEnd, setPeriodEnd] = useState(plusDaysIso(14));
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null); // { i, n, naam } tijdens per-project genereren
  const [losseTaken, setLosseTaken] = useState(''); // niet-projectgebonden taken → eigen dossier

  // ── Dossiers (aan mij + door mij) ──
  const [mine, setMine] = useState({ toMe: [], given: [] });
  // Collega-overdrachten (read-only bekijken + exporteren)
  const [collegaKeuze, setCollegaKeuze] = useState(null);
  const [collegaGiven, setCollegaGiven] = useState(null); // null = nog niets geladen
  const [collegaLaden, setCollegaLaden] = useState(false);
  const laadCollega = async (c) => {
    setCollegaKeuze(c); setCollegaGiven(null);
    if (!c?.email) return;
    setCollegaLaden(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/collega?email=${encodeURIComponent(c.email)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setCollegaGiven(d.given || []);
    } catch (e) { setError(`Overdracht van ${c.name || c.email} laden mislukt: ${e.message}`); setCollegaGiven([]); }
    finally { setCollegaLaden(false); }
  };
  const [loadingMine, setLoadingMine] = useState(true);
  const [editDossier, setEditDossier] = useState(null); // { id, dossier, extra_notes }
  const [reanalyzing, setReanalyzing] = useState(null); // handover-id dat opnieuw analyseert (#2)
  const [chatText, setChatText] = useState({});          // { [id]: tekst } — bijstuur-chat (#3)
  const [chatBusy, setChatBusy] = useState(null);         // handover-id dat een chat verwerkt

  const loadMine = useCallback(async () => {
    if (!userEmail) return;
    setLoadingMine(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/mine?email=${encodeURIComponent(userEmail)}`);
      const d = await leesJson(r, 'Overdrachten laden');
      setMine({ toMe: d.toMe || [], given: d.given || [] });
    } catch (e) { setError(e.message); } // niet stil: een lege lijst zou lijken op "geen overdrachten"
    finally { setLoadingMine(false); }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    // Test-backend schaalt naar 0 → de eerste request na inactiviteit valt weg met
    // "Failed to fetch" (cold start). Eén stille retry vangt dat op; de twee calls
    // laden onafhankelijk zodat een trage collega's-call de projecten niet blokkeert.
    const getJson = async (path, tries = 2) => {
      for (let i = 0; i < tries; i++) {
        try {
          const r = await authFetch(`${BACKEND_URL}${path}`);
          return await leesJson(r, 'Overdracht-gegevens laden');
        } catch (e) {
          if (i === tries - 1) throw e;
          await new Promise((res) => setTimeout(res, 1200)); // even wachten tot de container wakker is
        }
      }
    };
    (async () => {
      const [prRes, crRes, gvRes] = await Promise.allSettled([
        getJson(`/Api/overdracht/projects?email=${encodeURIComponent(userEmail)}`),
        getJson(`/Api/overdracht/colleagues`),
        getJson(`/Api/overdracht/gevers`),
      ]);
      if (gvRes.status === 'fulfilled') setGevers(gvRes.value?.gevers || {});
      if (prRes.status === 'fulfilled') {
        setProjects(prRes.value?.projects || []);
        setOffers(prRes.value?.offers || []);
      }
      if (crRes.status === 'fulfilled') {
        setColleagues((crRes.value?.colleagues || []).filter(c => c.email !== userEmail));
      }
      // Projecten óf collega's niet geladen = de tab is onbruikbaar; zeg dat (met reden) i.p.v. een lege lijst.
      const kapot = [prRes, crRes].find((x) => x.status === 'rejected');
      if (kapot) {
        setError(`Kon de gegevens niet laden — probeer het zo nog eens (de server werd mogelijk net wakker). ${kapot.reason?.message || ''}`.trim());
      }
      setLoadingProjects(false);
    })();
    loadMine();
  }, [userEmail, loadMine]);

  const toggleProject = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Actiepunten in de agenda van de waarnemer zetten is bewust een bewuste keuze en
  // niet de standaard: de afgeleide datums zaten er in de praktijk vaak naast, en ze
  // stonden in de agenda van een ánder — daar heb je zelf geen knop voor.
  // Agenda-planning bij genereren is vervallen: eerst controleren, dan per dossier inplannen.

  // Per geselecteerd project: korte opdrachtomschrijving + interne
  // verantwoordelijke (wiki-feedback). Gaan mee de AI-context in en komen als
  // vaste kopregels bovenaan het dossier.
  const [extraVelden, setExtraVelden] = useState({}); // { [projectId]: { omschrijving, verantwoordelijke } }

  const generate = async () => {
    setError(null);
    const selectable = [...projects, ...extraProjects.filter(e => !projects.some(p => p.id === e.id)), ...offers];
    if (!defaultTo && !selectable.filter(p => selected.has(p.id)).every(p => overrides[p.id])) {
      setError('Kies wie de projecten waarneemt (standaard-collega of per project).');
      return;
    }
    const chosen = selectable.filter(p => selected.has(p.id)).map(p => ({
      id: p.id, kind: p.kind, name: p.name, number: p.number, client: p.client, status: p.status, date: p.date,
      contact: p.contact || undefined,
      toEmail: overrides[p.id]?.email || undefined, toName: overrides[p.id]?.name || undefined,
      omschrijving: extraVelden[p.id]?.omschrijving?.trim() || undefined,
      verantwoordelijke: extraVelden[p.id]?.verantwoordelijke?.trim() || undefined,
    }));
    if (!chosen.length) { setError('Selecteer minimaal één project.'); return; }
    setGenerating(true);
    const act = startActivity(`Overdracht genereren (${chosen.length} projecten)`, { path: '/overdracht' });
    try {
      // Project-voor-project binnen één batch: voortgang zichtbaar in de knop,
      // en geen mega-request meer die halverwege de verbinding verliest.
      const batchId = crypto.randomUUID();
      let klaar = 0; let skip = 0; const skipNamen = []; const fouten = [];
      for (let i = 0; i < chosen.length; i += 1) {
        const proj = chosen[i];
        setGenProgress({ i: i + 1, n: chosen.length, naam: proj.name || '' });
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/overdracht/generate`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fromEmail: userEmail, fromName: userName,
              toEmail: proj.toEmail || defaultTo?.email,
              toName: proj.toName || defaultTo?.name,
              periodStart, periodEnd, projects: [proj], planAgenda: false, batchId,
              ...(i === chosen.length - 1 && losseTaken.trim() ? { losseTaken: losseTaken.trim() } : {}),
            }),
          });
          const d = await r.json().catch(() => ({}));
          if (r.status === 409) { skip += 1; skipNamen.push(proj.name || proj.id); continue; }
          if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
          klaar += (d.created || []).length;
          skip += (d.overgeslagen || []).length;
          skipNamen.push(...(d.overgeslagen || []));
        } catch (e) {
          fouten.push(`${proj.name || proj.id}: ${e.message}`);
        }
      }
      setGenProgress(null);
      act.done(`${klaar} dossiers klaar${skip ? ` · ${skip} overgeslagen (al actief)` : ''}${fouten.length ? ` · ${fouten.length} mislukt` : ''}`);
      const meldingen = [];
      if (skip) meldingen.push(`${skip} project(en) overgeslagen — daar loopt al een actieve overdracht voor: ${skipNamen.join(', ')}`);
      if (fouten.length) meldingen.push(`Mislukt: ${fouten.join(' · ')}`);
      if (meldingen.length) setError(meldingen.join(' — '));
      setSelected(new Set());
      setLosseTaken('');
      await loadMine();
      setTab(1); // direct naar "Door mij overgedragen" om te redigeren
    } catch (e) {
      // Generate is een lange operatie (Graph-searches + LLM per project); de POST slaagt
      // vaak server-side terwijl de response wegvalt → "Failed to fetch". We retryen de POST
      // bewust NIET (zou dubbel aanmaken); i.p.v. een harde fout herladen we de lijst, want
      // de dossiers staan er meestal gewoon.
      if (/failed to fetch|networkerror|load failed/i.test(e.message || '')) {
        // De POST kan server-side wél of NIET geslaagd zijn; we weten het niet zeker
        // (loadMine slikt fouten). Dus GEEN "aangemaakt" melden en de selectie BEHOUDEN.
        // We herladen de lijst zodat de gebruiker zelf ziet of het dossier er staat vóór
        // een eventuele nieuwe poging (voorkomt dubbel aanmaken).
        try { await loadMine(); } catch { /* lijst-refresh best-effort */ }
        setTab(1);
        setError('De verbinding viel weg tijdens genereren. Controleer bij "Door mij overgedragen" of de overdracht is aangemaakt vóór je het opnieuw probeert.');
        act.fail('verbinding weggevallen — controleer de lijst');
      } else { setError(e.message); act.fail(e.message); }
    }
    finally { setGenerating(false); setGenProgress(null); }
  };

  // Optimistic: pas de teruggegeven rij direct toe i.p.v. de hele lijst te
  // herladen (dat oogde als een pagina-refresh bij elk weggeklikt mailtje).
  const applyUpdated = (updated) => {
    setMine((m) => ({
      toMe: m.toMe.map((h) => (h.id === updated.id ? updated : h)),
      given: m.given.map((h) => (h.id === updated.id ? updated : h)),
    }));
    // Ook de collega-weergave bijwerken (bewerken door derden, 25 aug).
    setCollegaGiven((lijst) => (Array.isArray(lijst) ? lijst.map((h) => (h.id === updated.id ? updated : h)) : lijst));
  };
  useEffect(() => registreerOngedaanSneltoets(), []);
  const patchHandover = async (id, body) => {
    // Vorige status/vink onthouden: "teruggegeven" en "compleet" zijn één klik en verdwijnen uit het zicht.
    const vorig = [...mine.toMe, ...mine.given, ...(Array.isArray(collegaGiven) ? collegaGiven : [])].find((h) => h.id === id);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const updated = await r.json();
      if (!r.ok) throw new Error(updated.error || 'Opslaan mislukt');
      applyUpdated(updated);
      if (vorig && ('status' in body || 'voorbereid' in body)) {
        const terug = 'status' in body ? { status: vorig.status || 'active' } : { voorbereid: !!vorig.voorbereid };
        const tekst = 'status' in body ? 'Teruggegeven' : (body.voorbereid ? 'Gemarkeerd als compleet' : 'Markering compleet weggehaald');
        biedOngedaanAan(tekst, async () => {
          const t = await authFetch(`${BACKEND_URL}/Api/overdracht/${id}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(terug),
          });
          const hersteld = await t.json();
          if (!t.ok) throw new Error(hersteld.error || 'Terugzetten mislukt');
          applyUpdated(hersteld);
        });
      }
      return true;
    } catch (e) { setError(e.message); return false; }
  };
  const deleteHandover = async (id) => {
    if (!(await bevestig({ titel: 'Overdracht verwijderen?', tekst: 'Het dossier wordt verwijderd. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/overdracht/${id}`, { method: 'DELETE' });
      setMine((m) => ({ toMe: m.toMe.filter((h) => h.id !== id), given: m.given.filter((h) => h.id !== id) }));
    } catch (e) { setError(e.message); }
  };

  // 👤 Contactpersoon per dossier + mails opnieuw zoeken (contact is leidend).
  const [refreshingMails, setRefreshingMails] = useState(null);
  const [editContact, setEditContact] = useState(null); // { id, value }
  // Contactpersoon-suggesties (projectspiegel + eerdere overdrachten), per klant
  // gecachet — de wens "contactpersonen zoeken zoals in de offerte-tool".
  const [contactSuggesties, setContactSuggesties] = useState({});
  const laadContactSuggesties = useCallback(async (client) => {
    const key = String(client || '').trim().toLowerCase();
    if (key in contactSuggesties) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/contact-suggesties?client=${encodeURIComponent(client || '')}`);
      const d = await leesJson(r, 'Contactsuggesties laden');
      setContactSuggesties((m) => ({ ...m, [key]: d.suggesties || [] }));
    } catch (e) { setError(e.message); setContactSuggesties((m) => ({ ...m, [key]: [] })); }
  }, [contactSuggesties]);
  const [mailsOpen, setMailsOpen] = useState({});       // { [id]: true } — mails standaard ingeklapt (#3)
  const [returnNote, setReturnNote] = useState(null);   // { id, value } — terugkoppeling van de waarnemer (#5)
  const [returnSaving, setReturnSaving] = useState(null);
  const refreshMails = async (h, contactOverride) => {
    setRefreshingMails(h.id);
    try {
      const numberMatch = String(h.project_name || '').match(/^(?:Offerte|Kans)\s+(\S+)/);
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${h.id}/refresh-mails`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromEmail: h.from_email,
          contact: (contactOverride !== undefined ? contactOverride : h.contact) || null,
          number: numberMatch?.[1] || null,
          client: h.client_name || null,
        }),
      });
      const updated = await r.json();
      if (!r.ok) throw new Error(updated.error || 'Opnieuw zoeken mislukt');
      applyUpdated(updated);
    } catch (e) { setError(e.message); }
    finally { setRefreshingMails(null); }
  };

  // 📆 Agenda-items van dit dossier neerzetten of juist opruimen. Bewust een losse,
  // omkeerbare handeling: de datums komen uit een afleiding en zitten er soms naast.
  const [agendaBusy, setAgendaBusy] = useState(null);
  const agendaActie = async (h, plan) => {
    setAgendaBusy(h.id);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${h.id}/agenda`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Agenda bijwerken mislukt');
      if (d.restricted) {
        setError(`Geen toegang tot de agenda van ${h.to_name || h.to_email} — de items zijn niet ${plan ? 'gezet' : 'weggehaald'}. Een admin kan deze mailbox toevoegen via Admin → Instellingen → "Mail & agenda-toegang".`);
      }
      await loadMine();
    } catch (e) { setError(e.message); }
    finally { setAgendaBusy(null); }
  };

  // #2 — Hele dossier (mails + stand van zaken + open punten) opnieuw genereren.
  const reanalyze = async (h) => {
    setReanalyzing(h.id);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${h.id}/reanalyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact: h.contact || null }),
      });
      const updated = await r.json();
      if (!r.ok) throw new Error(updated.error || 'Opnieuw analyseren mislukt');
      applyUpdated(updated);
    } catch (e) { setError(e.message); }
    finally { setReanalyzing(null); }
  };

  // #3 — Dossier bijsturen met een instructie ("haal Previder eruit", "korter"…).
  const sendChat = async (h) => {
    const text = String(chatText[h.id] || '').trim();
    if (!text) return;
    setChatBusy(h.id);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${h.id}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Bijsturen mislukt');
      applyUpdated(data.handover || { ...h, dossier: data.dossier });
      setChatText((s) => ({ ...s, [h.id]: '' }));
    } catch (e) { setError(e.message); }
    finally { setChatBusy(null); }
  };

  const activeToMe = useMemo(() => mine.toMe.filter(h => h.status === 'active'), [mine.toMe]);
  const activeGiven = useMemo(() => mine.given.filter(h => h.status === 'active'), [mine.given]);
  // Actieve batches (één overdracht-actie = één batch) voor batch-terugdraaien/afronden.
  const givenBatches = useMemo(() => {
    const map = new Map();
    for (const h of activeGiven) {
      if (!map.has(h.batch_id)) map.set(h.batch_id, []);
      map.get(h.batch_id).push(h);
    }
    return [...map.entries()];
  }, [activeGiven]);

  const [editActie, setEditActie] = useState(null); // { dossierId, index|null, titel, datum }
  // Open taken van het project als spiekbriefje: einddata van taken zijn de
  // échte planning — klik = actiepunt met die datum.
  const [projectTaken, setProjectTaken] = useState({}); // { [dossierId]: 'loading' | taken[] }
  const toggleProjectTaken = async (h) => {
    if (projectTaken[h.id]) { setProjectTaken((v) => { const n = { ...v }; delete n[h.id]; return n; }); return; }
    setProjectTaken((v) => ({ ...v, [h.id]: 'loading' }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/project-taken?projectId=${encodeURIComponent(h.project_id || '')}`);
      const d = await leesJson(r, 'Projecttaken laden');
      setProjectTaken((v) => ({ ...v, [h.id]: d.taken || [] }));
    } catch (e) { setError(e.message); setProjectTaken((v) => ({ ...v, [h.id]: [] })); }
  };
  const bewaarActie = async (h) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${h.id}/actie-bijwerken`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          index: editActie.index, titel: editActie.titel.trim(), datum: editActie.datum || '',
          oudeTitel: editActie.index === null ? undefined : (parseJsonList(h.actions)[editActie.index] || {}).titel,
          oudeDatum: editActie.index === null ? undefined : ((parseJsonList(h.actions)[editActie.index] || {}).datum || ''),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const updated = { ...h, actions: JSON.stringify(d.acties || []) };
      applyUpdated(updated);
      setEditActie(null);
    } catch (e) { setError(`Actiepunt opslaan mislukt: ${e.message}`); }
  };

  // Eén actiepunt schrappen — de controleslag vóór (of na) het inplannen.
  // Stond het al in de agenda, dan verdwijnt dat event ook.
  const verwijderActie = async (h, index) => {
    if (!(await bevestig({ titel: 'Actiepunt verwijderen?', tekst: 'Stond het al in de agenda, dan verdwijnt dat event ook.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${h.id}/actie-verwijderen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ index, titel: (parseJsonList(h.actions)[index] || {}).titel, datum: (parseJsonList(h.actions)[index] || {}).datum || '' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const updated = { ...h, actions: JSON.stringify(d.acties || []) };
      applyUpdated(updated);
    } catch (e) { setError(`Actiepunt verwijderen mislukt: ${e.message}`); }
  };

  const [batchBusy, setBatchBusy] = useState(null); // batchId van de lopende actie
  const batchAction = async (batchId, action) => {
    if (action === 'undo' && !(await bevestig({ titel: 'Hele overdracht terugdraaien?', tekst: 'Alle dossiers van deze batch worden verwijderd.', knop: 'Terugdraaien', gevaarlijk: true }))) return;
    setBatchBusy(batchId);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/${action === 'undo' ? 'undo-batch' : 'close-batch'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId, fromEmail: userEmail }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Actie mislukt');
      await loadMine();
    } catch (e) { setError(e.message); }
    finally { setBatchBusy(null); }
  };

  const parseJsonList = (s) => { try { const v = JSON.parse(s || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
  const stripMd = (s) => String(s || '').replace(/^#+\s*/gm, '').replace(/\*\*/g, '').replace(/\r/g, '').trim();
  const esc = (s) => String(s || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

  // 📄 Exporteer een set dossiers naar Excel (één rij per project) — voor overzicht.
  const exportExcel = async (list, label) => {
    if (!list.length) return;
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Overdracht');
    ws.columns = [
      { header: 'Project', key: 'p', width: 34 },
      { header: 'Klant', key: 'k', width: 24 },
      { header: 'Contactpersoon', key: 'c', width: 20 },
      { header: 'Periode', key: 'per', width: 18 },
      { header: 'Waarnemer', key: 'w', width: 20 },
      { header: 'Stand van zaken', key: 's', width: 60 },
      { header: 'Acties', key: 'a', width: 40 },
      { header: 'Open punten', key: 'o', width: 40 },
      { header: 'Gekoppelde mails', key: 'm', width: 40 },
      { header: 'Notities van de PL', key: 'n', width: 40 },
      { header: 'Terugkoppeling waarnemer', key: 't', width: 40 },
    ];
    ws.getRow(1).font = { bold: true };
    for (const h of list) {
      ws.addRow({
        p: h.project_name, k: h.client_name || '', c: h.contact || '',
        per: `${fmtDate(h.period_start)} – ${fmtDate(h.period_end)}`,
        w: h.to_name || h.to_email || '',
        s: stripMd(h.dossier),
        n: h.extra_notes || '',
        t: h.return_notes || '',
        a: parseJsonList(h.actions).map((x) => `${fmtDate(x.datum)}: ${x.titel}`).join('\n'),
        o: parseJsonList(h.open_points).map((x) => `${x.titel}${x.vraag ? ` — ${x.vraag}` : ''}`).join('\n'),
        m: parseJsonList(h.mails).map((x) => `${fmtDate(x.datum)} · ${x.onderwerp}`).join('\n'),
      });
    }
    ws.eachRow((row) => row.eachCell((cell) => { cell.alignment = { vertical: 'top', wrapText: true }; }));
    const buf = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url; a.download = `Overdracht ${userName ? userName + ' — ' : ''}${label} ${todayIso()}.xlsx`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // 📄 PDF-download: server-side gegenereerd (Puppeteer, zelfde als callsheets) —
  // scherpe tekst én géén print-popup. Valt terug op het print-venster als de
  // backend hapert (cold start).
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfFallback, setPdfFallback] = useState(null); // { list, label } — na backend-fout
  const exportPdf = async (list, label, scope, vanEmail) => {
    if (!list.length || pdfBusy) return;
    setPdfBusy(true);
    setPdfFallback(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, ...(vanEmail ? { vanEmail } : {}) }),
      });
      if (!r.ok) throw new Error(`export-pdf ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Overdracht ${userName ? userName + ' — ' : ''}${label} ${todayIso()}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      // De print-fallback moet vanuit een VERSE klik komen (anders blokkeert de browser de popup —
      // de user-activation van de download-klik is verlopen na de await). Toon dus een knop.
      console.warn('[Overdracht] server-PDF mislukt, bied print-fallback aan:', err.message);
      setPdfFallback({ list, label });
    } finally {
      setPdfBusy(false);
    }
  };

  // 📄 Fallback: PDF via een print-venster (browser → 'Bewaar als PDF') —
  // dependency-vrij, gebruikt als de server-PDF niet lukt.
  const exportPdfPrint = (list, label) => {
    if (!list.length) return;
    const sections = list.map((h) => {
      const acties = parseJsonList(h.actions).map((x) => `<li>📅 ${esc(fmtDate(x.datum))} — ${esc(x.titel)}</li>`).join('');
      const punten = parseJsonList(h.open_points).map((x) => `<li>📬 ${esc(x.titel)}${x.vraag ? ` — <i>${esc(x.vraag)}</i>` : ''}</li>`).join('');
      const mails = parseJsonList(h.mails).map((x) => `<li>${esc(fmtDate(x.datum))} · ${esc(x.onderwerp)}${x.van ? ` — ${esc(String(x.van).split('@')[0])}` : ''}</li>`).join('');
      const dossier = esc(stripMd(h.dossier)).replace(/\n/g, '<br>');
      return `<section>
        <h2>${esc(h.project_name)}${h.client_name ? ` — ${esc(h.client_name)}` : ''}</h2>
        <div class="meta">${h.contact ? `👤 ${esc(h.contact)} · ` : ''}${esc(fmtDate(h.period_start))} – ${esc(fmtDate(h.period_end))} · waarnemer: ${esc(h.to_name || h.to_email || '')}</div>
        <div class="body">${dossier}</div>
        ${acties ? `<h3>Acties</h3><ul>${acties}</ul>` : ''}
        ${punten ? `<h3>Open punten</h3><ul>${punten}</ul>` : ''}
        ${h.extra_notes ? `<h3>Notities van ${esc(h.from_name || 'de PL')}</h3><div class="body">${esc(h.extra_notes).replace(/\n/g, '<br>')}</div>` : ''}
        ${h.return_notes ? `<h3>↩ Terugkoppeling van ${esc(h.to_name || 'de waarnemer')}</h3><div class="body">${esc(h.return_notes).replace(/\n/g, '<br>')}</div>` : ''}
        ${mails ? `<h3>Gekoppelde mails</h3><ul class="small">${mails}</ul>` : ''}
      </section>`;
    }).join('');
    const w = window.open('', '_blank');
    if (!w) { setError('Kon geen print-venster openen — sta pop-ups toe voor deze site.'); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Overdracht ${esc(userName ? userName + ' — ' : '')}${esc(label)} ${todayIso()}</title>
      <style>
        body{font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#1e293b;max-width:800px;margin:24px auto;padding:0 16px}
        h1{font-size:22px;margin:0 0 4px} .sub{color:#64748b;margin-bottom:24px}
        section{border:1px solid #e5e7eb;border-radius:8px;padding:16px 20px;margin-bottom:16px;page-break-inside:avoid}
        h2{font-size:16px;margin:0 0 4px} .meta{color:#64748b;font-size:12px;margin-bottom:10px}
        h3{font-size:13px;margin:14px 0 4px;color:#334155} ul{margin:4px 0;padding-left:20px} .small{font-size:12px;color:#475569}
        .body{white-space:pre-wrap}
        @media print{ section{border-color:#cbd5e1} }
      </style></head><body>
      <h1>Overdracht — ${esc(userName ? userName + ' · ' : '')}${esc(label)}</h1>
      <div class="sub">${list.length} project${list.length === 1 ? '' : 'en'} · gegenereerd ${new Date().toLocaleDateString('nl-NL')}</div>
      ${sections}
      <script>window.onload=function(){setTimeout(function(){window.print()},250)}</script>
      </body></html>`);
    w.document.close();
  };

  const ExportBar = ({ list, label, scope, van }) => (
    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
      <Button size="small" variant="outlined" disabled={!list.length}
        onClick={() => exportExcel(list, label)} sx={{ textTransform: 'none', fontWeight: 700 }}>
        ⬇ Excel
      </Button>
      <Button size="small" variant="outlined" disabled={!list.length || pdfBusy}
        onClick={() => exportPdf(list, label, scope, van)} sx={{ textTransform: 'none', fontWeight: 700 }}>
        {pdfBusy ? '… PDF' : '⬇ PDF'}
      </Button>
    </Stack>
  );

  // 📧 Mail-preview (zoals bij Frits): snelle popup i.p.v. Outlook openen.
  const [mailPreview, setMailPreview] = useState(null); // { title, from, loading, body, webLink }
  const openMailPreview = async (h, m) => {
    setMailPreview({ title: m.onderwerp || '(zonder onderwerp)', from: m.van, webLink: m.webLink, loading: true, body: '' });
    if (!m.id) { setMailPreview((p) => (p ? { ...p, loading: false, body: '(geen preview beschikbaar voor deze mail — open hem in Outlook)' } : p)); return; }
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overdracht/mail-body?ownerEmail=${encodeURIComponent(h.from_email)}&messageId=${encodeURIComponent(m.id)}`);
      const d = await leesJson(r, 'Mail ophalen');
      setMailPreview((p) => (p ? { ...p, loading: false, body: d.body || '(mailtekst niet beschikbaar)' } : p));
    } catch (e) {
      setMailPreview((p) => (p ? { ...p, loading: false, body: `(${e.message} — open hem in Outlook)` } : p));
    }
  };

  // Waarnemer achteraf wijzigen (bleek een andere PL al betrokken te zijn).
  const [editTo, setEditTo] = useState(null); // handover-id
  const changeWaarnemer = async (h, colleague) => {
    setEditTo(null);
    if (!colleague) return;
    await patchHandover(h.id, { to_email: colleague.email, to_name: colleague.name });
  };

  const DossierCard = ({ h, canEdit, canClose, viewOnly = false }) => {
    const acties = parseJsonList(h.actions);
    const mailsList = parseJsonList(h.mails);
    const openPunten = parseJsonList(h.open_points);
    const resolveOpenPunt = (idx, addToDossier) => {
      const punt = openPunten[idx];
      const rest = openPunten.filter((_p, i) => i !== idx);
      const body = { open_points: JSON.stringify(rest) };
      if (addToDossier && punt) {
        body.dossier = `${h.dossier || ''}\n- ⚠️ ${punt.titel}${punt.datum ? ` (${punt.datum})` : ''}${punt.vraag ? ` — ${punt.vraag}` : ''}`;
      }
      patchHandover(h.id, body);
    };
    return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, ...(h.voorbereid ? { bgcolor: '#f0fdf4', borderColor: '#bbf7d0' } : {}) }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography fontWeight={800} sx={{ flex: 1, minWidth: 200 }}>
          {/^rec[A-Za-z0-9]+$/.test(String(h.project_id || '')) ? (
            <Box component="span" onClick={() => navigate(`/projects/${encodeURIComponent(h.project_id)}`)}
              sx={{ cursor: 'pointer', color: '#4338ca', '&:hover': { textDecoration: 'underline' } }}
              title="Open de projectpagina">
              {h.project_number ? `${h.project_number} · ` : ''}{h.project_name}
            </Box>
          ) : `${h.project_number ? `${h.project_number} · ` : ''}${h.project_name}`}{h.client_name ? ` — ${h.client_name}` : ''}
        </Typography>
        <Chip size="small" label={`${fmtDate(h.period_start)} – ${fmtDate(h.period_end)}`} />
        {editContact?.id === h.id ? (
          <Autocomplete
            size="small" sx={{ minWidth: 260 }} multiple freeSolo autoHighlight openOnFocus
            options={contactSuggesties[String(h.client_name || '').trim().toLowerCase()] || []}
            value={editContact.value ? editContact.value.split(',').map((c) => c.trim()).filter(Boolean) : []}
            onChange={(_e, namen) => setEditContact({ id: h.id, value: namen.join(', ') })}
            renderTags={(waarden, getTagProps) => waarden.map((optie, index) => (
              <Chip size="small" label={optie} {...getTagProps({ index })} key={optie} />
            ))}
            renderInput={(params) => (
              <TextField {...params} autoFocus variant="standard" placeholder="contactperso(o)n(en)… (Enter voegt toe)"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditContact(null);
                }} />
            )}
            onBlur={() => {
              const v = (editContact.value || '').trim();
              setEditContact(null);
              if (v !== (h.contact || '')) refreshMails(h, v || null);
            }}
          />
        ) : (
          <Tooltip title="Contactperso(o)n(en) van dit traject — de mail-koppeling zoekt hierop (meerdere mag). Klik om aan te passen (zoekt daarna opnieuw).">
            <Chip size="small" label={`👤 ${h.contact || 'contact instellen'}`}
              onClick={() => { laadContactSuggesties(h.client_name); setEditContact({ id: h.id, value: h.contact || '' }); }}
              sx={{ bgcolor: h.contact ? '#f0fdf4' : '#fef3c7', color: h.contact ? '#166534' : '#92400e', fontWeight: 700, cursor: 'pointer' }} />
          </Tooltip>
        )}
        {editTo === h.id ? (
          <Autocomplete size="small" options={colleagues} sx={{ width: 220 }} autoHighlight openOnFocus
            getOptionLabel={(o) => o.name || ''}
            isOptionEqualToValue={(o, v) => o.email === v.email}
            onChange={(_e, v) => changeWaarnemer(h, v)}
            onBlur={() => setEditTo(null)}
            renderInput={(params) => <TextField {...params} autoFocus variant="standard" placeholder="nieuwe waarnemer…" />} />
        ) : (
          <Tooltip title={canEdit ? 'Klik om de waarnemer te wijzigen' : ''}>
            <Chip size="small" label={canEdit ? `→ ${h.to_name || h.to_email}` : `van ${h.from_name || h.from_email}`}
              onClick={canEdit ? () => setEditTo(h.id) : undefined}
              sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700, cursor: canEdit ? 'pointer' : 'default' }} />
          </Tooltip>
        )}
        {h.status !== 'active' && <Chip size="small" label="afgerond" color="success" />}
        {canEdit && (
          <>
            <Tooltip title="Dossier aanvullen/redigeren">
              <IconButton size="small" onClick={() => setEditDossier({
                id: h.id, dossier: h.dossier || '', extra_notes: h.extra_notes || '',
                creative: (String(h.dossier || '').match(/^\*\*Interne verantwoordelijke[^:\n]*:\*\*\s*(.+)$/m) || [])[1]?.trim() || '',
              })}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Dossier opnieuw analyseren (mails + stand van zaken; agenda-acties blijven ongewijzigd)">
              <IconButton size="small" disabled={reanalyzing === h.id} onClick={() => reanalyze(h)}>
                {reanalyzing === h.id ? <CircularProgress size={15} /> : <span style={{ fontSize: 15 }}>🔄</span>}
              </IconButton>
            </Tooltip>
            <Tooltip title="Overdracht verwijderen">
              <IconButton size="small" onClick={() => deleteHandover(h.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Tooltip>
          </>
        )}
        {canClose && h.status === 'active' && (
          <Button size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon />}
            onClick={() => patchHandover(h.id, { status: 'returned' })}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Afronden / teruggeven
          </Button>
        )}
        {/* ✔-vink van de gever: 'dit dossier is compleet/voorbereid' — Joanne's
            kleurcodering. Alleen op je eigen overdrachten. */}
        {canEdit && h.status === 'active' && (
          <Tooltip title={h.voorbereid ? 'Gemarkeerd als compleet — klik om terug te zetten' : 'Markeer als compleet (dossier is klaar)'}>
            <Button size="small" variant={h.voorbereid ? 'contained' : 'text'} color="success"
              onClick={() => patchHandover(h.id, { voorbereid: !h.voorbereid })}
              sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
              {h.voorbereid ? '✔ compleet' : '☐ compleet?'}
            </Button>
          </Tooltip>
        )}
        {/* Waarnemer: expliciet terug overdragen (knop was er niet op deze tab) */}
        {!canEdit && !viewOnly && h.status === 'active' && (
          <Button size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon />}
            onClick={async () => {
              if (returnNote?.id === h.id && returnNote.value?.trim()) {
                await patchHandover(h.id, { return_notes: returnNote.value });
              }
              await patchHandover(h.id, { status: 'returned' });
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            ↩ Ik draag dit terug over
          </Button>
        )}
        {h.status === 'returned' && (
          <Chip size="small" label="↩ terug overgedragen" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
        )}
      </Stack>
      {editDossier?.id === h.id ? (
        <Stack spacing={1}>
          <TextField multiline minRows={8} fullWidth value={editDossier.dossier}
            onChange={(e) => setEditDossier(d => ({ ...d, dossier: e.target.value }))}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }} />
          <Autocomplete size="small" freeSolo openOnFocus options={colleagues.map((c) => c.name)}
            value={editDossier.creative || ''}
            onInputChange={(_e, val) => setEditDossier((v) => ({ ...v, creative: val }))}
            renderInput={(params) => (
              <TextField {...params} variant="standard" label="Creative / interne verantwoordelijke (naast de PL)" placeholder="bv. een regisseur of creative" />
            )} sx={{ maxWidth: 380 }} />
          <TextField multiline minRows={2} fullWidth label="Extra notities (wat de data niet weet: afspraken, gevoeligheden…)"
            value={editDossier.extra_notes}
            onChange={(e) => setEditDossier(d => ({ ...d, extra_notes: e.target.value }))} />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" sx={{ textTransform: 'none', fontWeight: 700 }}
              onClick={async () => {
                // Creative als vaste kopregel in het dossier zetten/vervangen —
                // zo reist hij mee in export/print zonder apart datamodel-veld.
                let tekst = editDossier.dossier;
                const regel = editDossier.creative?.trim() ? `**Interne verantwoordelijke (naast de PL):** ${editDossier.creative.trim()}` : '';
                if (/^\*\*Interne verantwoordelijke[^:\n]*:\*\*.*$/m.test(tekst)) {
                  tekst = tekst.replace(/^\*\*Interne verantwoordelijke[^:\n]*:\*\*.*$/m, regel).replace(/\n{3,}/g, '\n\n').trim();
                } else if (regel) {
                  tekst = `${regel}\n\n${tekst}`;
                }
                await patchHandover(h.id, { dossier: tekst, extra_notes: editDossier.extra_notes });
                setEditDossier(null);
              }}>
              Opslaan
            </Button>
            <Button size="small" onClick={() => setEditDossier(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          </Stack>
        </Stack>
      ) : (
        <Box sx={{ '& h2': { fontSize: 15, mt: 1.5, mb: 0.5 }, '& p, & li': { fontSize: 14 }, '& ul': { mt: 0.25, mb: 0.5, pl: 3 } }}>
          <ReactMarkdown>{h.dossier || '_Geen dossier._'}</ReactMarkdown>
          {canEdit && (
            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} alignItems="center">
              <TextField size="small" fullWidth
                placeholder="Stuur het dossier bij… (bv. 'haal Previder eruit', 'korter', 'voeg toe dat…')"
                value={chatText[h.id] || ''}
                onChange={(e) => setChatText((s) => ({ ...s, [h.id]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendChat(h); } }}
                disabled={chatBusy === h.id} />
              <Button size="small" variant="outlined" disabled={chatBusy === h.id || !(chatText[h.id] || '').trim()}
                onClick={() => sendChat(h)} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {chatBusy === h.id ? <CircularProgress size={15} /> : 'Bijsturen'}
              </Button>
            </Stack>
          )}
          {(
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {acties.map((a, i) => (
                  <Chip key={i} size="small" label={`${a.agendaEventId ? '📆' : '📅'} ${fmtDate(a.datum)} · ${a.titel}`}
                    onClick={viewOnly ? undefined : () => setEditActie({ dossierId: h.id, index: i, titel: a.titel || '', datum: a.datum || '' })}
                    onDelete={viewOnly ? undefined : () => verwijderActie(h, i)}
                    sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 600, maxWidth: 420, cursor: viewOnly ? 'default' : 'pointer' }} />
                ))}
                {!viewOnly && <Chip size="small" label="+ actiepunt" variant="outlined"
                  onClick={() => setEditActie({ dossierId: h.id, index: null, titel: '', datum: '' })}
                  sx={{ fontWeight: 700, cursor: 'pointer' }} />}
              </Stack>
              {!viewOnly && (<Button size="small" variant="text" onClick={() => toggleProjectTaken(h)}
                sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5 }}>
                📋 {Array.isArray(projectTaken[h.id]) ? 'Verberg projecttaken' : 'Planning: taken van dit project'}
              </Button>)}
              {projectTaken[h.id] === 'loading' && <CircularProgress size={14} sx={{ ml: 1 }} />}
              {Array.isArray(projectTaken[h.id]) && (
                <Box sx={{ mt: 0.5, p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  {projectTaken[h.id].length === 0 ? (
                    <Typography variant="caption" color="text.secondary">Geen open taken bij dit project (of geen Airtable-project).</Typography>
                  ) : projectTaken[h.id].map((t, ti) => (
                    <Stack key={ti} direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
                      <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.naam}{t.wie ? ` · ${t.wie}` : ''}{t.status ? ` · ${t.status}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {t.eind ? `⏰ ${fmtDate(t.eind)}` : 'geen einddatum'}
                      </Typography>
                      <Button size="small" variant="text" disabled={!t.eind}
                        onClick={() => setEditActie({ dossierId: h.id, index: null, titel: t.naam, datum: String(t.eind).slice(0, 10) })}
                        sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, flexShrink: 0 }}>
                        → actiepunt
                      </Button>
                    </Stack>
                  ))}
                </Box>
              )}
              {editActie?.dossierId === h.id && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }} alignItems={{ sm: 'center' }}>
                  <TextField size="small" variant="standard" autoFocus placeholder="actiepunt…" sx={{ flex: 1, minWidth: 200 }}
                    value={editActie.titel} onChange={(e) => setEditActie((v) => ({ ...v, titel: e.target.value }))} />
                  <TextField size="small" variant="standard" type="date" value={editActie.datum || ''}
                    onChange={(e) => setEditActie((v) => ({ ...v, datum: e.target.value }))} sx={{ width: 150 }} />
                  <Button size="small" variant="contained" disabled={!editActie.titel.trim()}
                    onClick={() => bewaarActie(h)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    {editActie.index === null ? 'Toevoegen' : 'Opslaan'}
                  </Button>
                  <Button size="small" onClick={() => setEditActie(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
                </Stack>
              )}
              {/* De agenda van de waarnemer is van de waarnemer: zowel gever als
                  ontvanger kan deze items neerzetten én weer weghalen. */}
              {!viewOnly && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" disabled={agendaBusy === h.id}
                  startIcon={agendaBusy === h.id ? <CircularProgress size={13} /> : <EventOutlinedIcon fontSize="small" />}
                  onClick={() => agendaActie(h, !acties.some((a) => a.agendaEventId))}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {acties.some((a) => a.agendaEventId)
                    ? `Uit agenda ${h.to_name || 'waarnemer'} halen`
                    : `In agenda ${h.to_name || 'waarnemer'} zetten`}
                </Button>
                <Typography variant="caption" color="text.disabled">
                  Hele-dag-items, met een 🔁 in de titel. Kloppen de datums niet? Haal ze weg — het dossier blijft gewoon staan.
                </Typography>
              </Stack>
              )}
            </Box>
          )}
          {openPunten.length > 0 && (
            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              {openPunten.map((p, i) => (
                <Alert key={i} severity="warning" sx={{ py: 0.5 }}
                  action={canEdit ? (
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" color="inherit" onClick={() => resolveOpenPunt(i, true)} sx={{ textTransform: 'none', fontWeight: 700 }}>→ In dossier</Button>
                      <Button size="small" color="inherit" onClick={() => resolveOpenPunt(i, false)} sx={{ textTransform: 'none' }}>Negeren</Button>
                    </Stack>
                  ) : undefined}>
                  <Typography variant="body2" fontWeight={700}>📬 {p.titel}{p.datum ? ` (${fmtDate(p.datum)})` : ''}</Typography>
                  {p.vraag && <Typography variant="body2">{p.vraag}</Typography>}
                </Alert>
              ))}
              {canEdit && (
                <Typography variant="caption" color="text.disabled">
                  Onbeantwoorde punten uit je inbox (niet-gearchiveerde mails) — zet ze in het dossier of negeer ze.
                </Typography>
              )}
            </Stack>
          )}
          {(mailsList.length > 0 || h.contact) && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {/* Standaard ingeklapt — je gaat er toch niet in zoeken; klik om uit te vouwen. */}
                <Typography variant="caption" fontWeight={800}
                  onClick={() => setMailsOpen((p) => ({ ...p, [h.id]: !p[h.id] }))}
                  sx={{ color: '#64748b', letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none' }}>
                  {mailsOpen[h.id] ? '▾' : '▸'} 📧 GEKOPPELDE MAILS ({mailsList.length})
                </Typography>
                <Tooltip title={`Opnieuw zoeken op ${h.contact || 'nummer/klant'}`}>
                  <IconButton size="small" disabled={refreshingMails === h.id} onClick={() => refreshMails(h)} sx={{ p: 0.25 }}>
                    {refreshingMails === h.id ? <CircularProgress size={13} /> : <span style={{ fontSize: 13 }}>↻</span>}
                  </IconButton>
                </Tooltip>
              </Stack>
              {mailsOpen[h.id] && (
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {mailsList.slice(0, 10).map((m, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={0.5}
                    sx={{ '&:hover .mail-del': { opacity: 0.7 } }}>
                    <Typography variant="caption" color="text.secondary" noWrap
                      onClick={() => openMailPreview(h, m)}
                      sx={{ cursor: 'pointer', '&:hover': { color: '#4338ca' } }}>
                      {fmtDate(m.datum)} · <span style={{ color: '#4338ca' }}>{m.onderwerp || '(zonder onderwerp)'}</span>
                      {m.van ? ` — ${String(m.van).split('@')[0]}` : ''}
                    </Typography>
                    {m.webLink && (
                      <Tooltip title="Open in Outlook">
                        <IconButton size="small" component="a" href={m.webLink} target="_blank" rel="noopener noreferrer"
                          sx={{ p: 0.25 }}>
                          <span style={{ fontSize: 12 }}>↗</span>
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Hoort niet bij dit dossier — weghalen">
                      <IconButton className="mail-del" size="small"
                        onClick={() => patchHandover(h.id, { mails: JSON.stringify(mailsList.filter((_x, xi) => xi !== i)) })}
                        sx={{ opacity: 0, transition: 'opacity .15s', p: 0.25 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
              )}
            </Box>
          )}
          {h.extra_notes && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>Notities van {h.from_name || 'de PL'}:</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{h.extra_notes}</Typography>
            </Alert>
          )}
          {/* #5 Terugkoppeling: de waarnemer tikt hier de terug-overdracht; de gever leest 'm terug. */}
          {!canEdit && !viewOnly ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" fontWeight={800} sx={{ color: '#64748b', letterSpacing: 0.5 }}>
                ↩ TERUGKOPPELING VOOR {(h.from_name || 'DE PL').toUpperCase()}
              </Typography>
              <TextField fullWidth multiline minRows={2} size="small" sx={{ mt: 0.5 }}
                placeholder={`Wat is er gebeurd tijdens je waarneming? Deze terug-overdracht ziet ${h.from_name || 'de PL'} terug.`}
                value={returnNote?.id === h.id ? returnNote.value : (h.return_notes || '')}
                onChange={(e) => setReturnNote({ id: h.id, value: e.target.value })} />
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                <Button size="small" variant="contained"
                  disabled={returnSaving === h.id || returnNote?.id !== h.id}
                  onClick={async () => {
                    setReturnSaving(h.id);
                    const ok = await patchHandover(h.id, { return_notes: returnNote.value });
                    if (ok) setReturnNote(null);   // bij een fout blijft de getypte tekst staan
                    setReturnSaving(null);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {returnSaving === h.id ? 'Opslaan…' : 'Terugkoppeling opslaan'}
                </Button>
              </Stack>
            </Box>
          ) : (h.return_notes && (
            <Alert severity="success" icon={false} sx={{ mt: 1.5 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>↩ Terugkoppeling van {h.to_name || 'de waarnemer'}:</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{h.return_notes}</Typography>
            </Alert>
          ))}
        </Box>
      )}
    </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      <PageHeader
        icon="swap_horiz"
        title="Overdracht"
        subtitle="Draag projecten tijdelijk over aan een collega — de tool bouwt per project een stand-van-zaken-dossier uit taken, deadlines, draaidagen en meeting-notes."
      />
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {pdfFallback && (
        <Alert severity="warning" onClose={() => setPdfFallback(null)} sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" sx={{ textTransform: 'none', fontWeight: 700 }}
              onClick={() => { exportPdfPrint(pdfFallback.list, pdfFallback.label); setPdfFallback(null); }}>
              Open printversie
            </Button>
          }>
          Direct downloaden lukte even niet (server werd mogelijk net wakker). Open de printversie of probeer de PDF-knop zo nog eens.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}
        variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
        <Tab label="Overdragen" sx={{ textTransform: 'none', fontWeight: 700 }} />
        <Tab label={`Door mij overgedragen${activeGiven.length ? ` (${activeGiven.length})` : ''}`} sx={{ textTransform: 'none', fontWeight: 700 }} />
        <Tab label={`Aan mij overgedragen${activeToMe.length ? ` (${activeToMe.length})` : ''}`} sx={{ textTransform: 'none', fontWeight: 700 }} />
        <Tab label="Collega's" sx={{ textTransform: 'none', fontWeight: 700 }} />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>Stap 1 — Periode & wie neemt waar</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, mt: 1 }}>
            <TextField size="small" type="date" label="Van" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Tot en met" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Autocomplete size="small" options={colleagues} sx={{ minWidth: { xs: '100%', sm: 260 } }}
              getOptionLabel={(o) => o.name || ''} value={defaultTo}
              isOptionEqualToValue={(o, v) => o.email === v.email}
              onChange={(_e, v) => setDefaultTo(v)}
              renderInput={(params) => <TextField {...params} label="Waarnemer (standaard)" placeholder="collega…" />} />
          </Stack>

          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>Stap 2 — Kies je projecten</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Je actieve projecten (als PL of creative lead) én je openstaande offertes (Gripp, status Verzonden). Wil je iets aan een ándere collega geven? Pas de waarnemer per regel aan.
          </Typography>
          {/* Handmatig toevoegen: elk actief project, ook zonder PL-koppeling */}
          <Autocomplete size="small" options={searchOpts} value={null} blurOnSelect clearOnBlur
            loading={searchLoading} filterOptions={(x) => x} sx={{ mb: 1.5, maxWidth: 480 }}
            getOptionLabel={(o) => `${o.number || ''} — ${o.name}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onInputChange={(_e, val, reason) => { if (reason === 'input') searchProjects(val); }}
            onChange={(_e, val) => { if (val) addExtraProject(val); }}
            renderOption={(props, o) => (
              <Box component="li" {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{[o.number, o.client, o.status].filter(Boolean).join(' · ')}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="➕ Project handmatig toevoegen" placeholder="Zoek op naam, nummer of klant…" />}
            noOptionsText="Typ om te zoeken" />

          {loadingProjects ? <CircularProgress size={22} /> : projects.length === 0 && extraProjects.length === 0 && offers.length === 0 ? (
            <Alert severity="info">Geen actieve projecten of openstaande offertes gevonden waar jij projectleider, creative lead of accountmanager van bent — voeg ze eventueel handmatig toe via de zoeker hierboven.</Alert>
          ) : (() => {
            // Nieuwste eerst (server-sortering); oude nooit-afgesloten projecten
            // inklappen. Geselecteerde en handmatig toegevoegde blijven zichtbaar.
            const allProjects = [...projects, ...extraProjects.filter(e => !projects.some(p => p.id === e.id))];
            const RECENT_N = 8;
            const shownProjects = showOlder ? allProjects
              : allProjects.filter((p, i) => i < RECENT_N || selected.has(p.id) || p.rol === 'handmatig');
            const hiddenCount = allProjects.length - shownProjects.length;
            return (
            <Stack spacing={0.75}>
              {[...shownProjects, ...(hiddenCount > 0 ? [{ id: '__more__' }] : []), ...offers].map((p, idx) => (
                p.id === '__more__' ? (
                  <Button key="__more__" size="small" onClick={() => setShowOlder(true)}
                    sx={{ textTransform: 'none', alignSelf: 'flex-start', color: 'text.secondary' }}>
                    ▾ Toon {hiddenCount} oudere projecten
                  </Button>
                ) : (
                <React.Fragment key={p.id}>
                  {/* Kopje boven het eerste offerte-item */}
                  {p.kind === 'offerte' && offers[0]?.id === p.id && (
                    <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 800, letterSpacing: 0.5, mt: 1 }}>
                      💼 OPENSTAANDE OFFERTES (SALES) — follow-up laten bewaken
                    </Typography>
                  )}
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap
                    sx={{ p: 1, borderRadius: 1.5, border: '1px solid #eef2f6',
                      bgcolor: selected.has(p.id) ? '#eef2ff' : p.kind === 'offerte' ? '#fffbeb' : '#fff' }}>
                    <Checkbox size="small" checked={selected.has(p.id)} onChange={() => toggleProject(p.id)} sx={{ p: 0.5 }} />
                    <Box sx={{ flex: 1, minWidth: 180 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[p.kind === 'offerte' ? (p.contact ? `👤 ${p.contact}` : null) : p.number, p.client, p.status, p.rol ? `🎨 ${p.rol}` : null, p.date].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                    {selected.has(p.id) && (
                      <Stack spacing={0.25} sx={{ width: { xs: '100%', sm: 260 } }}>
                        <Autocomplete size="small" options={colleagues}
                          getOptionLabel={(o) => o.name || ''} value={overrides[p.id] || null}
                          isOptionEqualToValue={(o, v) => o.email === v.email}
                          onChange={(_e, v) => setOverrides(prev => ({ ...prev, [p.id]: v }))}
                          renderInput={(params) => <TextField {...params} placeholder={defaultTo ? `→ ${defaultTo.name}` : 'waarnemer…'} variant="standard" />} />
                        <TextField size="small" fullWidth multiline maxRows={3} placeholder="korte opdrachtomschrijving (optioneel)"
                          value={extraVelden[p.id]?.omschrijving || ''}
                          onChange={(e) => setExtraVelden(prev => ({ ...prev, [p.id]: { ...prev[p.id], omschrijving: e.target.value } }))}
                          inputProps={{ style: { fontSize: '0.85rem' } }} />
                        <Autocomplete size="small" freeSolo openOnFocus fullWidth
                          options={colleagues.map((c) => c.name)}
                          value={extraVelden[p.id]?.verantwoordelijke || ''}
                          onInputChange={(_e, val) => setExtraVelden(prev => ({ ...prev, [p.id]: { ...prev[p.id], verantwoordelijke: val } }))}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="interne verantwoordelijke, bv. regisseur/creative (optioneel)"
                              inputProps={{ ...params.inputProps, style: { fontSize: '0.85rem' } }} />
                          )} />
                      </Stack>
                    )}
                  </Stack>
                </React.Fragment>
                )
              ))}
            </Stack>
            );
          })()}

          <Divider sx={{ my: 2 }} />
          {/* Verdeling: zie in één oogopslag of de last eerlijk verdeeld is. */}
          {selected.size > 0 && (() => {
            const selectable = [...projects, ...extraProjects.filter(e => !projects.some(p => p.id === e.id)), ...offers];
            const telling = new Map();
            selectable.filter(p => selected.has(p.id)).forEach(p => {
              const wie = overrides[p.id]?.name || defaultTo?.name || '—';
              telling.set(wie, (telling.get(wie) || 0) + 1);
            });
            return (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                ⚖️ Verdeling: {[...telling.entries()].map(([wie, n]) => `${wie} ×${n}`).join(' · ')}
              </Typography>
            );
          })()}

          <TextField fullWidth multiline minRows={2} size="small" sx={{ mb: 1.5 }}
            label="Overige taken (niet projectgebonden, optioneel)"
            placeholder="bv. planten water geven, post afhandelen, wekelijkse rapportage draaien…"
            value={losseTaken} onChange={(e) => setLosseTaken(e.target.value)} />

          {/* Agenda-items gaan bewust NIET automatisch mee: eerst per dossier de
              actiepunten controleren (kruisje = schrappen), dan pas inplannen. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            📅 Agenda-items worden níet automatisch ingepland. Controleer na het genereren per dossier de
            actiepunten (schrap wat niet klopt met het kruisje) en zet ze daarna pas in de agenda met de knop op het dossier.
          </Typography>
          <Button variant="contained" size="large" disabled={generating || selected.size === 0}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={generate} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {generating
              ? (genProgress ? `Dossier ${genProgress.i}/${genProgress.n} — ${String(genProgress.naam).slice(0, 32)}…` : 'Dossiers genereren…')
              : `Genereer overdracht (${selected.size} project${selected.size === 1 ? '' : 'en'})`}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Na het genereren kun je elk dossier nog redigeren en aanvullen — doe dat even, de data weet niet alles.
          </Typography>
        </Paper>
      )}

      {tab === 1 && (
        loadingMine ? <CircularProgress size={22} /> : activeGiven.length === 0 && mine.given.length === 0 ? (
          <Alert severity="info">Je hebt nog niets overgedragen.</Alert>
        ) : (
          <Stack spacing={1.5}>
            <ExportBar list={mine.given} label="door mij" scope="given" />
            {/* Batch-acties: hele overdracht in één keer afronden of terugdraaien */}
            {givenBatches.map(([batchId, items]) => (
              <Paper key={batchId} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                    {(() => {
                      // Eén batch kan naar meerdere collega's gaan (vakantie-overdracht):
                      // toon ze allemaal, niet alleen de eerste.
                      const namen = [...new Set(items.map((x) => x.to_name || x.to_email))];
                      const wie = namen.length > 2 ? `${namen.slice(0, 2).join(', ')} +${namen.length - 2}` : namen.join(' & ');
                      return `Overdracht aan ${wie} — ${items.length} project${items.length === 1 ? '' : 'en'}`;
                    })()}
                    {items[0].period_end ? ` · t/m ${fmtDate(items[0].period_end)}` : ''}
                  </Typography>
                  <Button size="small" variant="outlined" disabled={batchBusy === batchId}
                    startIcon={batchBusy === batchId ? <CircularProgress size={13} /> : <CheckCircleOutlineIcon />}
                    onClick={() => batchAction(batchId, 'close')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Alles afronden (ik ben terug)
                  </Button>
                  <Button size="small" color="error" variant="text" disabled={batchBusy === batchId}
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => batchAction(batchId, 'undo')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Terugdraaien
                  </Button>
                </Stack>
                {items.map((h) => <React.Fragment key={h.id}>{DossierCard({ h, canEdit: true, canClose: true })}</React.Fragment>)}
              </Paper>
            ))}
            {/* Dossiers zonder (actieve) batch — afgerond of van vóór de batches */}
            {mine.given.filter((h) => !givenBatches.some(([, items]) => items.some((x) => x.id === h.id)))
              .map((h) => <React.Fragment key={h.id}>{DossierCard({ h, canEdit: true, canClose: true })}</React.Fragment>)}
          </Stack>
        )
      )}

      {/* Mail-preview (snel lezen zonder Outlook) */}
      <Dialog open={Boolean(mailPreview)} onClose={() => setMailPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} component="div">{mailPreview?.title}</Typography>
          <Typography variant="caption" color="text.secondary">{mailPreview?.from}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {mailPreview?.loading
            ? <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={22} /></Stack>
            : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{mailPreview?.body}</Typography>}
        </DialogContent>
        <DialogActions>
          {mailPreview?.webLink && (
            <Button href={mailPreview.webLink} target="_blank" rel="noopener" sx={{ textTransform: 'none' }}>
              Open in Outlook
            </Button>
          )}
          <Button onClick={() => setMailPreview(null)} variant="contained" sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>

      {tab === 2 && (
        loadingMine ? <CircularProgress size={22} /> : mine.toMe.length === 0 ? (
          <Alert severity="info">Er is (nog) niets aan jou overgedragen.</Alert>
        ) : (
          <Stack spacing={1.5}>
            <ExportBar list={mine.toMe} label="aan mij" scope="tome" />
            {activeToMe.length > 0 && (
              <Alert severity="warning" icon={<SwapHorizIcon />}>
                Je neemt momenteel <b>{activeToMe.length}</b> project{activeToMe.length === 1 ? '' : 'en'} waar.
              </Alert>
            )}
            {mine.toMe.map((h) => <React.Fragment key={h.id}>{DossierCard({ h, canEdit: false, canClose: false })}</React.Fragment>)}
          </Stack>
        )
      )}

      {/* Collega-overdrachten: de hele overdracht van iemand anders bekijken
          (read-only) — bv. door Joanne's vakantie-overdracht heen lopen, of
          hem als Excel/PDF meenemen. Bewerken blijft aan gever/waarnemer. */}
      {tab === 3 && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Autocomplete size="small" sx={{ width: 300 }} autoHighlight openOnFocus
              options={[...colleagues].sort((a, b) => geverAantal(b.email) - geverAantal(a.email) || (a.name || '').localeCompare(b.name || ''))}
              value={collegaKeuze} isOptionEqualToValue={(o, v) => o.email === v.email}
              getOptionLabel={(o) => { if (!o) return ''; const n = geverAantal(o.email); return `${o.name || o.email}${n ? ` (${n})` : ''}`; }}
              onChange={(_e, c) => { setCollegaPl(null); laadCollega(c); }}
              renderInput={(params) => <TextField {...params} label="Overdracht van collega" placeholder="Kies een collega…" />} />
            {collegaGiven && collegaGiven.some((h) => h.status === 'active') && (
              <Autocomplete size="small" sx={{ width: 230 }} autoHighlight openOnFocus
                options={[...new Map(collegaGiven.filter((h) => h.status === 'active').map((h) => [h.to_email || h.to_name, { email: h.to_email || '', name: h.to_name || h.to_email || '' }])).values()]}
                value={collegaPl} isOptionEqualToValue={(o, v) => (o.email || o.name) === (v.email || v.name)}
                getOptionLabel={(o) => o?.name || o?.email || ''}
                onChange={(_e, v) => setCollegaPl(v)}
                renderInput={(params) => <TextField {...params} label="Waarnemend PL" placeholder="Alle waarnemers" />} />
            )}
            <TextField size="small" sx={{ width: 240 }} value={collegaZoek}
              onChange={(e) => setCollegaZoek(e.target.value)}
              label="Zoeken" placeholder="Projectnummer of klant…" />
          </Stack>
          {collegaLaden && <CircularProgress size={22} />}
          {collegaGiven && !collegaLaden && (
            collegaGiven.length === 0 ? (
              <Alert severity="info">{collegaKeuze?.name || 'Deze collega'} heeft niets overgedragen.</Alert>
            ) : (
              <>
                <ExportBar list={collegaGiven} label={`van ${collegaKeuze?.name || collegaKeuze?.email}`} scope="given" van={collegaKeuze?.email} />
                {(() => {
                  const q = collegaZoek.trim().toLowerCase();
                  const past = (h) => {
                    if (collegaPl && (h.to_email || h.to_name) !== (collegaPl.email || collegaPl.name)) return false;
                    if (q && !`${h.project_number || ''} ${h.project_name || ''} ${h.client_name || ''}`.toLowerCase().includes(q)) return false;
                    return true;
                  };
                  const map = new Map();
                  collegaGiven.filter((h) => h.status === 'active' && past(h)).forEach((h) => {
                    if (!map.has(h.batch_id)) map.set(h.batch_id, []);
                    map.get(h.batch_id).push(h);
                  });
                  if (map.size === 0) return <Alert severity="info">Geen dossiers gevonden met deze filters.</Alert>;
                  return [...map.entries()].map(([batchId, items]) => (
                    <Paper key={batchId} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                        {(() => {
                          const namen = [...new Set(items.map((x) => x.to_name || x.to_email))];
                          return `Overdracht aan ${namen.join(' & ')} — ${items.length} project${items.length === 1 ? '' : 'en'}`;
                        })()}
                        {items[0].period_end ? ` · t/m ${fmtDate(items[0].period_end)}` : ''}
                      </Typography>
                      <Stack spacing={1.5}>
                        {items.map((h) => <React.Fragment key={h.id}>{DossierCard({ h, canEdit: true, canClose: false })}</React.Fragment>)}
                      </Stack>
                    </Paper>
                  ));
                })()}
                {collegaGiven.filter((h) => h.status !== 'active').length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    + {collegaGiven.filter((h) => h.status !== 'active').length} afgerond(e)/teruggegeven dossier(s) — zitten wél in de export.
                  </Typography>
                )}
              </>
            )
          )}
        </Stack>
      )}
    </Box>
  );
}
